import type { ParkingFilterGroup, ParkingPropertyFilter } from '@repo/domain';
import type { MyIcon } from '@repo/icons';
import {
  ArrowRotate24Icon,
  CarIcon,
  ElectricVehicleCharge100Icon,
  ElectricVehicleCharge200Icon,
  ElectricVehicleCharge300Icon,
  ElectricVehicleCharge400Icon,
  ElectricVehicleChargeOver400Icon,
  ElectricVehicleIcon,
  GabledRoofIcon,
  GarageIcon,
  GasPumpIcon,
  MaximizeIcon,
  MinimizeIcon,
  PadlockBigClosedIcon,
  SemiTruckIcon,
  SunCloudIcon,
  TruckNoLimitsIcon,
  TruckUnder12tIcon,
  TruckUnder7tIcon,
  WheelchairIcon,
} from '@repo/icons';

type AssertSubsetOfFilters<T extends ParkingPropertyFilter> = T;

type EvChargingFilterChild = AssertSubsetOfFilters<
  | 'evChargingAc'
  | 'evChargingDcUnder100kW'
  | 'evChargingDc100To200kW'
  | 'evChargingDc200To300kW'
  | 'evChargingDc300To400kW'
  | 'evChargingDcOver400kW'
>;

type SizeFilterChild = AssertSubsetOfFilters<'xxl' | 'truckUnder7t' | 'truck7To12t' | 'truckOver12t' | 'truckSemi'>;

type CoveredFilterChild = AssertSubsetOfFilters<'carport' | 'indoor'>;

type SecuredFilterChild = AssertSubsetOfFilters<'guarded' | 'lockable'>;

type ParkingFilterGroupChild = EvChargingFilterChild | SizeFilterChild | CoveredFilterChild | SecuredFilterChild;

export type ParkingFilterTopKey = ParkingFilterGroup | Exclude<ParkingPropertyFilter, ParkingFilterGroupChild>;

interface ParkingFilterEntry {
  nameTranslationKey: string;
  icon: MyIcon;
}

export type ParkingFilterDictionary = {
  readonly [K in ParkingFilterTopKey]: K extends 'evCharging'
    ? ParkingFilterEntry & { subProperties: Record<EvChargingFilterChild, ParkingFilterEntry> }
    : K extends 'size'
      ? ParkingFilterEntry & { subProperties: Record<SizeFilterChild, ParkingFilterEntry> }
      : K extends 'covered'
        ? ParkingFilterEntry & { subProperties: Record<CoveredFilterChild, ParkingFilterEntry> }
        : K extends 'secured'
          ? ParkingFilterEntry & { subProperties: Record<SecuredFilterChild, ParkingFilterEntry> }
          : ParkingFilterEntry;
};

export const parkingFilterDictionary: ParkingFilterDictionary = {
  evCharging: {
    nameTranslationKey: 'parking_search:filter.ev_charging',
    icon: ElectricVehicleIcon,
    subProperties: {
      evChargingAc: {
        nameTranslationKey: 'parking_search:filter.ev_charging_ac',
        icon: ElectricVehicleIcon,
      },
      evChargingDcUnder100kW: {
        nameTranslationKey: 'parking_search:filter.ev_charging_dc_under_100_kw',
        icon: ElectricVehicleCharge100Icon,
      },
      evChargingDc100To200kW: {
        nameTranslationKey: 'parking_search:filter.ev_charging_dc_100_to_200_kw',
        icon: ElectricVehicleCharge200Icon,
      },
      evChargingDc200To300kW: {
        nameTranslationKey: 'parking_search:filter.ev_charging_dc_200_to_300_kw',
        icon: ElectricVehicleCharge300Icon,
      },
      evChargingDc300To400kW: {
        nameTranslationKey: 'parking_search:filter.ev_charging_dc_300_to_400_kw',
        icon: ElectricVehicleCharge400Icon,
      },
      evChargingDcOver400kW: {
        nameTranslationKey: 'parking_search:filter.ev_charging_dc_over_400_kw',
        icon: ElectricVehicleChargeOver400Icon,
      },
    },
  },
  disabled: {
    nameTranslationKey: 'parking_search:filter.disabled',
    icon: WheelchairIcon,
  },
  open24h: {
    nameTranslationKey: 'parking_search:filter.open_24h',
    icon: ArrowRotate24Icon,
  },
  outdoor: {
    nameTranslationKey: 'parking_search:filter.outdoor',
    icon: SunCloudIcon,
  },
  covered: {
    nameTranslationKey: 'parking_search:filter.covered',
    icon: GabledRoofIcon,
    subProperties: {
      carport: {
        nameTranslationKey: 'parking_search:filter.carport',
        icon: GarageIcon,
      },
      indoor: {
        nameTranslationKey: 'parking_search:filter.indoor',
        icon: GabledRoofIcon,
      },
    },
  },
  secured: {
    nameTranslationKey: 'parking_search:filter.secured',
    icon: PadlockBigClosedIcon,
    subProperties: {
      guarded: {
        nameTranslationKey: 'parking_search:filter.guarded',
        icon: PadlockBigClosedIcon,
      },
      lockable: {
        nameTranslationKey: 'parking_search:filter.lockable',
        icon: PadlockBigClosedIcon,
      },
    },
  },
  size: {
    nameTranslationKey: 'parking_search:filter.size',
    icon: MaximizeIcon,
    subProperties: {
      xxl: {
        nameTranslationKey: 'parking_search:filter.xxl',
        icon: CarIcon,
      },
      truckUnder7t: {
        nameTranslationKey: 'parking_search:filter.truck_under_7_t',
        icon: TruckUnder7tIcon,
      },
      truck7To12t: {
        nameTranslationKey: 'parking_search:filter.truck_7_to_12_t',
        icon: TruckUnder12tIcon,
      },
      truckOver12t: {
        nameTranslationKey: 'parking_search:filter.truck_over_12_t',
        icon: TruckNoLimitsIcon,
      },
      truckSemi: {
        nameTranslationKey: 'parking_search:filter.truck_semi',
        icon: SemiTruckIcon,
      },
    },
  },
  lpgFriendly: {
    nameTranslationKey: 'parking_search:filter.lpg_friendly',
    icon: GasPumpIcon,
  },
  nonCompact: {
    nameTranslationKey: 'parking_search:filter.compact',
    icon: MinimizeIcon,
  },
} as const;
